//*Libraries imports
import { ArrowLeft, ArrowRight } from "lucide-react";
//*Components imports
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

type ActivityHeaderProps = {
  client: {
    name: string;
    image?: string;
    productName: string;
  };
  currentPhase: {
    index: number;
    name: string;
    totalPhases: number;
  };
  stats: {
    completed: number;
    pending: number;
    overdue: number;
    notStarted: number;
    completed_phase: number;
    pending_phase: number;
    overdue_phase: number;
    notStarted_phase: number;
  };
  phases: any[];
  onPrevPhase: () => void;
  onNextPhase: () => void;
  onSelectPhase: (index: number) => void;
};

export function ActivityHeader(props: ActivityHeaderProps) {
  // Calculate pagination window
  const MAX_VISIBLE_PHASES = 10;
  let startIdx = Math.max(
    0,
    props.currentPhase.index - Math.floor(MAX_VISIBLE_PHASES / 2)
  );
  let endIdx = startIdx + MAX_VISIBLE_PHASES;

  if (endIdx > props.phases.length) {
    endIdx = props.phases.length;
    startIdx = Math.max(0, endIdx - MAX_VISIBLE_PHASES);
  }

  const visiblePhases = props.phases
    .slice(startIdx, endIdx)
    .map((p, i) => ({ ...p, originalIndex: startIdx + i }));

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm mb-6 grid grid-cols-[auto_1fr] items-center gap-12">
      {/* Left: User Info */}
      <div className="flex items-center gap-5">
        <Avatar className="size-24 rounded-xl shadow-sm">
          <AvatarImage
            src={props.client.image}
            alt={props.client.name}
            className="object-cover"
          />
          <AvatarFallback className="rounded-xl text-2xl">
            {props.client.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col gap-1">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Nome:
            </p>
            <h3 className="font-semibold text-gray-700 text-base">
              {props.client.name}
            </h3>
          </div>

          <div className="mt-1">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Produto:
            </p>
            <p className="font-semibold text-gray-700 text-base">
              {props.client.productName}
            </p>
          </div>
        </div>
      </div>

      {/* Right: Phase Info & Navigation */}
      <div className="flex flex-col items-center justify-center w-full">
        <div className="text-center w-full">
          <h2 className="text-xl font-bold text-gray-800 mb-1">
            Fases ({props.phases.length})
          </h2>
          <p className="text-gray-600 font-medium underline decoration-gray-400 underline-offset-4 mb-3 text-sm">
            {props.currentPhase.index + 1}. {props.currentPhase.name}
          </p>

          <div className="flex items-center justify-center gap-4 text-base text-gray-400 mb-4" style={{flexWrap:'wrap'}}>
            <span className="flex items-center gap-1">
              Concluídas:{" "}
              <span className="font-medium text-gray-400">
                {String(props.stats.completed_phase).padStart(2, "0")}
              </span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1">
              Pendentes:{" "}
              <span className="font-medium text-gray-400">
                {String(props.stats.pending_phase).padStart(2, "0")}
              </span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1">
              Atrasadas:{" "}
              <span className="font-medium text-gray-400">
                {String(props.stats.overdue_phase).padStart(2, "0")}
              </span>
            </span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1">
              Não Iniciadas:{" "}
              <span className="font-medium text-gray-400">
                {String(props.stats.notStarted_phase).padStart(2, "0")}
              </span>
            </span>
          </div>
        </div>

        {/* Phase Navigation Buttons */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Button
              variant="default"
              size="icon"
              onClick={props.onPrevPhase}
              disabled={props.currentPhase.index === 0}
              className="size-7 bg-blue-400 hover:bg-blue-500 rounded-md shadow-none"
            >
              <ArrowLeft className="size-4" />
            </Button>

            <div className="flex gap-1.5">
              {visiblePhases.map((phase) => (
                <button
                  key={phase.originalIndex}
                  onClick={() => props.onSelectPhase(phase.originalIndex)}
                  className={`size-7 flex items-center justify-center rounded-md text-[13px] font-semibold transition-all border ${
                    props.currentPhase.index === phase.originalIndex
                      ? "bg-blue-400 text-white border-blue-400"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {String(phase.originalIndex + 1).padStart(2, "0")}
                </button>
              ))}
            </div>

            <Button
              variant="default"
              size="icon"
              onClick={props.onNextPhase}
              disabled={props.currentPhase.index === props.phases.length - 1}
              className="size-7 bg-blue-400 hover:bg-blue-500 rounded-md shadow-none"
            >
              <ArrowRight className="size-4" />
            </Button>
          </div>
          {/* Pagination Dots */}
          {props.phases.length > MAX_VISIBLE_PHASES && (
            <div className="flex gap-1 mt-1 justify-center w-full">
              <div className="size-1.5 rounded-full bg-blue-600"></div>
              <div className="size-1.5 rounded-full bg-gray-300"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
